import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Package, ScanLine, CheckCircle2, AlertTriangle, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import adminApi from "@/lib/adminApi";

const fmtDate = (iso) => { if (!iso) return "—"; try { const hasTime = typeof iso === "string" && iso.length > 10; const d = new Date(hasTime ? iso : iso + "T00:00:00"); const b = `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`; return hasTime ? `${b} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}` : b; } catch { return iso; } };
const RS = { EXPECTED: ["Esperado", "bg-slate-100 text-slate-600"], RECEIVED: ["Recibido", "bg-emerald-100 text-emerald-700"], MISSING: ["Faltante", "bg-rose-100 text-rose-700"], UNEXPECTED: ["No esperado", "bg-amber-100 text-amber-800"] };
const ST = { "ENVIADO": "bg-sky-100 text-sky-700", "RECIBIDO PARCIAL": "bg-amber-100 text-amber-800", "RECIBIDO": "bg-emerald-100 text-emerald-700", "BORRADOR": "bg-slate-100 text-slate-500", "CANCELADO": "bg-rose-100 text-rose-600" };

function Reception({ shipment, onClose, onChanged }) {
  const [sh, setSh] = useState(shipment);
  const [imei, setImei] = useState("");
  const [busy, setBusy] = useState(false);
  const scan = async () => {
    if (!imei.trim()) return;
    setBusy(true);
    try {
      const r = await adminApi.post(`/admin/shipments/${sh.id}/scan`, { imei: imei.trim() });
      const map = { received: "✓ Recibido", duplicate: "Ya estaba recibido", unexpected: "⚠ No esperado" };
      toast[r.data.scan_result === "received" ? "success" : "warning"](map[r.data.scan_result] || "Escaneado");
      setSh(r.data); setImei("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setBusy(false); }
  };
  const confirm = async () => {
    setBusy(true);
    try {
      const unexpected = (sh.items || []).filter((i) => i.recv_status === "UNEXPECTED").map((i) => i.imei);
      const r = await adminApi.post(`/admin/shipments/${sh.id}/confirm`, { approve_unexpected: unexpected.length && window.confirm(`¿Incorporar ${unexpected.length} equipo(s) NO esperado(s) al inventario?`) ? unexpected : [] });
      toast.success(`Recepción confirmada · ${r.data.created_count} equipo(s) al inventario`);
      setSh(r.data); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setBusy(false); }
  };
  const s = sh.summary || {};
  const closed = sh.status === "RECIBIDO" || sh.status === "CANCELADO";
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="recv-dialog">
        <DialogHeader><DialogTitle>Recepción · {sh.id}</DialogTitle></DialogHeader>
        <div className="space-y-3 max-h-[75vh] overflow-y-auto">
          <p className="text-sm text-slate-600">{sh.owner_name} ({sh.owner_code}) · {sh.reference || "sin ref"} · {fmtDate(sh.date)}</p>
          <div className="grid grid-cols-4 gap-2 text-center">
            {[["Esperados", s.expected, "text-slate-700"], ["Recibidos", s.received, "text-emerald-700"], ["Faltantes", s.missing, "text-rose-600"], ["No esper.", s.unexpected, "text-amber-600"]].map(([l, v, c]) => (
              <div key={l} className="rounded-xl bg-slate-50 py-2"><p className="text-[10px] text-slate-400 uppercase">{l}</p><p className={`text-lg font-bold ${c}`} data-testid={`recv-count-${l}`}>{v ?? 0}</p></div>
            ))}
          </div>
          {!closed && (
            <div className="flex gap-2">
              <div className="relative flex-1"><ScanLine className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input data-testid="recv-imei" value={imei} onChange={(e) => setImei(e.target.value)} onKeyDown={(e) => e.key === "Enter" && scan()} placeholder="Escanear / escribir IMEI" className="h-11 rounded-xl pl-9" autoFocus />
              </div>
              <Button data-testid="recv-scan-btn" onClick={scan} disabled={busy} className="h-11 rounded-xl bg-slate-900 hover:bg-slate-800">Cotejar</Button>
            </div>
          )}
          <div className="rounded-xl border border-slate-200 divide-y divide-slate-100">
            {(sh.items || []).map((it) => {
              const [lbl, cls] = RS[it.recv_status] || ["", ""];
              return (
                <div key={it.line_id} data-testid={`recv-item-${it.imei || it.line_id}`} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
                  <div className="min-w-0">
                    <p className="font-medium text-slate-800 truncate">{it.model} {it.capacity} {it.color ? `· ${it.color}` : ""}
                      <span className={`ml-1.5 rounded-full px-1.5 py-0.5 text-[9px] font-bold align-middle ${it.condition_type === "USADO" ? "bg-orange-100 text-orange-700" : "bg-blue-100 text-blue-700"}`}>{it.condition_type === "USADO" ? "USADO" : "NUEVO"}</span>
                    </p>
                    <p className="text-xs text-slate-400">IMEI {it.imei || "—"} · Mi precio: {it.my_price != null ? `$${it.my_price}` : "Pendiente"}{it.condition_type === "USADO" && it.battery_percent != null ? ` · 🔋 ${it.battery_percent}%${it.charge_cycles != null ? ` · ${it.charge_cycles} ciclos` : ""}` : ""}</p>
                  </div>
                  <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${cls}`}>{lbl}</span>
                </div>
              );
            })}
          </div>
          {!closed && <Button data-testid="recv-confirm" onClick={confirm} disabled={busy || !s.received} className="h-11 w-full rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirmar recepción (solo recibidos entran al inventario)"}</Button>}
          {closed && <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-emerald-800 text-sm flex items-center gap-2" data-testid="recv-closed"><CheckCircle2 className="w-4 h-4" /> Preaviso {sh.status}. Recibido por {sh.received_by || "—"}.</div>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ShipmentsAdminTab() {
  const [rows, setRows] = useState(null);
  const [status, setStatus] = useState("ENVIADO");
  const [open, setOpen] = useState(null);
  const load = (st = status) => { setRows(null); adminApi.get(`/admin/shipments?status=${st}`).then((r) => setRows(r.data)).catch(() => setRows([])); };
  useEffect(() => { load(status); /* eslint-disable-next-line */ }, [status]);
  const FILTERS = [["ENVIADO", "Pendientes"], ["RECIBIDO PARCIAL", "Parciales"], ["RECIBIDO", "Recibidos"], ["TODOS", "Todos"]];
  const openOne = async (id) => { const r = await adminApi.get(`/admin/shipments/${id}`); setOpen(r.data); };
  return (
    <div data-testid="shipments-panel">
      <div className="flex flex-wrap gap-2 mb-5">
        {FILTERS.map(([k, l]) => (
          <button key={k} data-testid={`ship-filter-${k}`} onClick={() => setStatus(k)} className={`h-9 px-4 rounded-full text-sm font-medium transition-colors ${status === k ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{l}</button>
        ))}
      </div>
      {rows === null ? <div className="flex justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div> : rows.length === 0 ? (
        <p className="text-sm text-slate-400 py-10 text-center" data-testid="ship-empty">Sin preavisos en esta categoría.</p>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {rows.map((sh) => {
            const s = sh.summary || {};
            return (
              <div key={sh.id} data-testid={`ship-row-${sh.id}`} className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="flex items-start justify-between gap-2">
                  <div><p className="font-semibold text-slate-900 flex items-center gap-1.5"><Package className="w-4 h-4 text-slate-400" /> {sh.id}</p><p className="text-xs text-slate-500 mt-0.5">{sh.owner_name} ({sh.owner_code}) · {sh.reference || "sin ref"} · {fmtDate(sh.date)}</p></div>
                  <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${ST[sh.status] || "bg-slate-100"}`}>{sh.status}</span>
                </div>
                <p className="text-xs text-slate-500 mt-2">Esperados {s.expected} · Recibidos {s.received} · Faltantes {s.missing} · No esper. {s.unexpected}</p>
                <Button data-testid={`ship-open-${sh.id}`} onClick={() => openOne(sh.id)} className="mt-3 h-9 rounded-lg text-xs bg-slate-900 hover:bg-slate-800"><ScanLine className="w-3.5 h-3.5 mr-1" /> Abrir recepción</Button>
              </div>
            );
          })}
        </div>
      )}
      {open && <Reception shipment={open} onClose={() => setOpen(null)} onChanged={() => load()} />}
    </div>
  );
}
